import { parsePhoneNumberFromString } from "libphonenumber-js";
import { z } from "zod";

import {
  guardedProductFieldDtl,
  parseGuardedProductFieldDtl,
} from "../mergeTags/productFieldDtl";

import { MERGE_TAG_PATTERN } from "@/services/mergeTags";

export const SUGGESTION_TEXT_MAX_LENGTH = 25;

export function suggestionTextHasMergeTag(text: string): boolean {
  return text.replace(MERGE_TAG_PATTERN, "") !== text;
}

export const MAX_SUGGESTIONS = 11;

const MERGE_TAG_ONLY_PATTERN = new RegExp(`^${MERGE_TAG_PATTERN.source}$`);
const HTTP_URL_PATTERN = /^https?:\/\//i;
const DTL_TAG_PATTERN = /\{%.*?%\}/g;
/** E.164: a leading +, a non-zero country code, up to 15 digits total. */
const E164_PATTERN = /^\+[1-9]\d{1,14}$/;
const PHONE_SEPARATORS = /[\s()\-.]/g;

/**
 * Strips the separators the placeholder invites — `+1 (555) 000-0000` becomes
 * `+15550000000`. Applied when validating and when emitting the wire value, not
 * while typing, so the field never fights the marketer mid-entry.
 */
export function normalizePhoneNumber(raw: string): string {
  return raw.replace(PHONE_SEPARATORS, "");
}

/**
 * On-blur display formatting: `5550000000` → `+1 555 000 0000`, adding the
 * country code a bare number omits. `formatInternational` keeps the `+CC` and
 * drops the national trunk prefix, so non-US numbers stay dialable. A complete
 * number is prettified; anything
 * shorter (a partial number, a merge tag) is returned unchanged so the field
 * never discards what was typed. `isPossible` (length) rather than `isValid`
 * (real assignment) keeps it lenient. Wire normalization to E.164 still happens
 * in `normalizePhoneNumber`.
 */
export function formatPhoneNumber(raw: string): string {
  const parsed = parsePhoneNumberFromString(raw, { defaultCountry: "US" });
  if (!parsed?.isPossible()) return raw;
  return parsed.formatInternational();
}

export const suggestedActionSchema = z.discriminatedUnion("type", [
  // `id` is a stable client-side key for React/dnd, minted in
  // `createSuggestedAction`. It lives in authoring state but is stripped before
  // the wire (see `toWireSuggestions`). Optional so hand-built/legacy rows parse.
  z
    .object({
      type: z.literal("REPLY"),
      text: z.string(),
      id: z.string().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("OPEN_URL"),
      text: z.string(),
      url: z.string(),
      id: z.string().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("DIAL_PHONE"),
      text: z.string(),
      phoneNumber: z.string(),
      id: z.string().optional(),
    })
    .strict(),
]);

export function createProductSuggestion(
  model: string,
  index = 0,
): TextComposerSuggestedAction {
  return {
    type: "OPEN_URL",
    text: guardedProductFieldDtl(model, index, "name"),
    url: guardedProductFieldDtl(model, index, "item_url"),
    id: crypto.randomUUID(),
  };
}

export type TextComposerSuggestedAction = z.infer<typeof suggestedActionSchema>;

export type TextComposerSuggestedActionType =
  TextComposerSuggestedAction["type"];

export const suggestedActionsSchema = z.array(suggestedActionSchema);

export const SUGGESTION_TYPE_LABELS: Record<
  TextComposerSuggestedActionType,
  string
> = {
  OPEN_URL: "Open URL",
  REPLY: "Reply",
  DIAL_PHONE: "Dial phone",
};

export const SUGGESTION_TYPES = Object.keys(
  SUGGESTION_TYPE_LABELS,
) as TextComposerSuggestedActionType[];

export function createSuggestedAction(
  type: TextComposerSuggestedActionType,
): TextComposerSuggestedAction {
  switch (type) {
    case "REPLY":
      return { type: "REPLY", text: "", id: crypto.randomUUID() };
    case "OPEN_URL":
      return { type: "OPEN_URL", text: "", url: "", id: crypto.randomUUID() };
    case "DIAL_PHONE":
      return {
        type: "DIAL_PHONE",
        text: "",
        phoneNumber: "",
        id: crypto.randomUUID(),
      };
  }
}

export const SUGGESTION_ERROR_FIELDS = ["text", "url", "phoneNumber"] as const;

export type SuggestionRowErrors = Partial<
  Record<(typeof SUGGESTION_ERROR_FIELDS)[number], string>
>;

export type SuggestedActionErrors = Record<number, SuggestionRowErrors>;

function validateSuggestion(
  suggestion: TextComposerSuggestedAction,
): SuggestionRowErrors {
  const errors: SuggestionRowErrors = {};

  const text = suggestion.text.trim();
  const countedText = text
    .replace(DTL_TAG_PATTERN, "")
    .replace(MERGE_TAG_PATTERN, "");
  if (text.length === 0) {
    errors.text = "Button text is required";
  } else if (countedText.length > SUGGESTION_TEXT_MAX_LENGTH) {
    errors.text = `Button text cannot exceed ${SUGGESTION_TEXT_MAX_LENGTH} characters`;
  }

  if (suggestion.type === "OPEN_URL") {
    const url = suggestion.url.trim();
    if (url.length === 0) {
      errors.url = "URL is required";
    } else if (
      !MERGE_TAG_ONLY_PATTERN.test(url) &&
      !HTTP_URL_PATTERN.test(url) &&
      parseGuardedProductFieldDtl(url) === null
    ) {
      errors.url = "URL must start with http:// or https://";
    }
  }

  if (suggestion.type === "DIAL_PHONE") {
    const phoneNumber = normalizePhoneNumber(suggestion.phoneNumber.trim());
    if (phoneNumber.length === 0) {
      errors.phoneNumber = "Phone number is required";
    } else if (!E164_PATTERN.test(phoneNumber)) {
      errors.phoneNumber =
        "Enter a number in international format, e.g. +15550000000";
    }
  }

  return errors;
}

export function validateSuggestions(
  suggestions: TextComposerSuggestedAction[],
): SuggestedActionErrors {
  const errors: SuggestedActionErrors = {};

  suggestions.forEach((suggestion, index) => {
    const rowErrors = validateSuggestion(suggestion);
    if (Object.keys(rowErrors).length > 0) errors[index] = rowErrors;
  });

  return errors;
}
